import { createApi } from "@reduxjs/toolkit/query/react";
import { RESTAURANT, TagId, TagType } from "../../configs";
import {
  RestaurantDetails,
  IWriteRestaurant,
  PaginatedResponse,
  SearchByDisplayName,
  Restaurant,
  HttpMethod,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const restaurantApi = createApi({
  reducerPath: RESTAURANT.reducerPath,
  baseQuery: createBaseQuery(RESTAURANT.baseUrl),
  tagTypes: RESTAURANT.tagTypes,
  endpoints(builder) {
    return {
      toggleRestaurantIsActiveStatus: builder.mutation<
        void,
        { restaurantId: string; isDisabled: boolean }
      >({
        query: ({ restaurantId, isDisabled }) => ({
          url: `/${restaurantId}/active`,
          method: HttpMethod.PATCH,
          params: { isDisabled },
        }),
        invalidatesTags: () => [{ type: TagType.Restaurant, id: TagId.LIST }],
      }),

      fetchRestaurants: builder.query<
        PaginatedResponse<Restaurant>,
        SearchByDisplayName
      >({
        query: (searchQuery) => {
          return {
            url: "",
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Restaurant, id: TagId.LIST }],
      }),

      fetchRestaurantDetails: builder.query<RestaurantDetails, string>({
        query: (restaurantId) => {
          return {
            url: `/${restaurantId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, restaurantId) => [
          { type: TagType.Restaurant, id: restaurantId },
        ],
      }),

      createRestaurant: builder.mutation<RestaurantDetails, IWriteRestaurant>({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.Restaurant, id: TagId.ALL_LIST },
          { type: TagType.Restaurant, id: TagId.LIST },
        ],
      }),

      updateRestaurant: builder.mutation<
        void,
        { restaurantId: string; body: IWriteRestaurant }
      >({
        query: ({ restaurantId, body }) => ({
          url: `/${restaurantId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { restaurantId }) => [
          { type: TagType.Restaurant, id: TagId.ALL_LIST },
          { type: TagType.Restaurant, id: TagId.LIST },
          { type: TagType.Restaurant, id: restaurantId },
        ],
      }),

      updateRestaurantLogo: builder.mutation<
        void,
        { restaurantId: string; file: File }
      >({
        query: ({ restaurantId, file }) => {
          const formData = new FormData();
          formData.append("logo", file);

          return {
            url: `/${restaurantId}/logo`,
            method: HttpMethod.PATCH,
            body: formData,
          };
        },
        invalidatesTags: (_result, _error, { restaurantId }) => [
          { type: TagType.Restaurant, id: restaurantId },
        ],
      }),

      updateRestaurantBanner: builder.mutation<
        void,
        { restaurantId: string; file: File }
      >({
        query: ({ restaurantId, file }) => {
          const formData = new FormData();
          formData.append("banner", file);

          return {
            url: `/${restaurantId}/banner`,
            method: HttpMethod.PATCH,
            body: formData,
          };
        },
        invalidatesTags: (_result, _error, { restaurantId }) => [
          { type: TagType.Restaurant, id: restaurantId },
        ],
      }),

      fetchAllRestaurants: builder.query<Restaurant[], string>({
        query: (displayName) => {
          return {
            url: `/all`,
            method: HttpMethod.GET,
            params: { displayName },
          };
        },
        providesTags: () => [{ type: TagType.Restaurant, id: TagId.ALL_LIST }],
      }),

      deleteRestaurant: builder.mutation<void, string>({
        query: (restaurantId) => ({
          url: `/${restaurantId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, restaurantId) => [
          { type: TagType.Restaurant, id: TagId.ALL_LIST },
          { type: TagType.Restaurant, id: TagId.LIST },
          { type: TagType.Restaurant, id: restaurantId },
        ],
      }),
    };
  },
});

export const {
  useFetchRestaurantsQuery,
  useToggleRestaurantIsActiveStatusMutation,
  useCreateRestaurantMutation,
  useFetchRestaurantDetailsQuery,
  useUpdateRestaurantMutation,
  useUpdateRestaurantLogoMutation,
  useUpdateRestaurantBannerMutation,
  useFetchAllRestaurantsQuery,
  useDeleteRestaurantMutation,
} = restaurantApi;

export { restaurantApi };
